import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { AssignmentList } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentList'
import { AssignmentsManager } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager'
import { BaseCard } from 'modules/cost_centers/components/CostCenterAssigments/components/Cards/BaseCard'
import { DeleteModal } from 'modules/cost_centers/components/CostCenterAssigments/components/DeleteModal'
import { CostCenterAssignment } from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

interface Props {
  allAssignments: CostCenterAssignment[]
  assignments: CostCenterAssignment[]
  resourceId: number
  resourceType: string
  openOnRender?: boolean
  canManageAssignments: boolean
  onSave?: () => void
  onDelete?: () => void
  onToggleEdit: (value: boolean) => void
  refetchQueries?: TypedDocumentNode[]
}

export const MutationCard = ({
  allAssignments,
  assignments,
  resourceId,
  resourceType,
  openOnRender,
  canManageAssignments,
  onSave,
  onDelete,
  onToggleEdit,
  refetchQueries,
}: Props) => {
  const [editable, setEditable] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)

  const handleOnSave = () => {
    onSave?.()

    setEditable(false)
  }

  const handleDelete = () => {
    setEditable(false)
    setDeleteModal(false)

    onDelete?.()
  }

  const options = {
    onEdit: () => setEditable(true),
    onDelete: () => setDeleteModal(true),
  }

  const ids = assignments.map((assignment) => assignment.id)

  useEffect(() => {
    if (openOnRender) {
      // This is opening the assignment to allow the edit
      setEditable(true)
    }
  }, [openOnRender])

  useEffect(() => {
    // This is a callback to the parent component, to be able to manage the parent
    // when a box is being edited (for example, hiding the button for create a new assignment)
    onToggleEdit(editable)
  }, [editable])

  return (
    <>
      <BaseCard focus={editable}>
        <Box paddingX="s24" paddingY="s20">
          <AssignmentList
            assignments={assignments}
            {...(!editable && canManageAssignments && { options })}
          />
        </Box>

        {editable && (
          <>
            <Box
              width="full"
              height="s1"
              border={{ top: { style: 'solid', width: 's1', color: 'grey400' } }}
            />

            <AssignmentsManager
              allAssignments={allAssignments}
              assignments={assignments}
              title={i18n.t('finance.cost_centers.assignments.editable_card.title')}
              description={i18n.t('finance.cost_centers.assignments.editable_card.description')}
              resourceId={resourceId}
              resourceType={resourceType}
              onCancel={() => setEditable(false)}
              onDelete={handleDelete}
              onSave={handleOnSave}
              refetchQueries={refetchQueries}
            />
          </>
        )}
      </BaseCard>

      {deleteModal && (
        <DeleteModal
          ids={ids}
          onClose={() => {
            setDeleteModal(false)
          }}
          onSubmit={handleDelete}
          refetchQueries={refetchQueries}
        />
      )}
    </>
  )
}
